import { PlusCircleOutlined } from '@ant-design/icons';
import { IgnoreIdentifierKind } from '@eservices/shared/constant';
import { API } from '@eservices/shared/type';
import { Form, Space } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../../../../hooks/translationHook';
import { SecondaryButton } from '../../../../Buttons';
import { Header3 } from '../../../../Headers';
import DeleteButton from '../Common/DeleteButton';
import EmptyTextIgnore from './EmptyTextIgnore';
import RegexIgnore from './RegexIgnore';
import TextIgnore from './TextIgnore';

const Title = styled.div`
    margin: 20px 0px 40px 0px;
    text-align: center;
`;

const OrBlock = styled.div`
    margin: 20px 0;
    font-style: italic;
`;

interface IgnoreOverviewProps {
    isReadOnly: boolean;
    ignoreIdentifiers: API.PDFIgnoreIdentifier[];
    onAddNewIgnoreChoice: () => void;
    onUpdateIgnoreIdentifier: (order: number, value: string) => void;
    onDeleteIgnoreIdentifier: (order: number) => void;
}

const IgnoreOverview: React.FC<IgnoreOverviewProps> = ({
    isReadOnly,
    onAddNewIgnoreChoice,
    ignoreIdentifiers,
    onUpdateIgnoreIdentifier,
    onDeleteIgnoreIdentifier,
}) => {
    const { t, tCommon } = useTranslation('workflow');

    const fields = ignoreIdentifiers.map((identifier, index) => ({
        name: [index, 'value'],
        value: identifier.value,
    }));

    const onFormValuesChange = (changedValues: unknown) => {
        const order = Number(Object.keys(changedValues)[0]);
        const value = changedValues[order].value;
        onUpdateIgnoreIdentifier(order, value);
    };

    return (
        <div>
            <Title>
                <Header3>{t('ignore_overview_title')}</Header3>
            </Title>
            <Form fields={fields} onValuesChange={onFormValuesChange}>
                {ignoreIdentifiers.map((identifier, index) => {
                    let component: React.ReactNode;
                    switch (identifier.kind) {
                        case IgnoreIdentifierKind.EMPTY_TEXT:
                            component = <EmptyTextIgnore identifier={identifier} />;
                            break;
                        case IgnoreIdentifierKind.TEXT:
                            component = <TextIgnore isReadOnly={isReadOnly} order={index} identifier={identifier} />;
                            break;
                        case IgnoreIdentifierKind.REGEX:
                            component = <RegexIgnore isReadOnly={isReadOnly} order={index} identifier={identifier} />;
                            break;
                        default:
                            break;
                    }
                    return (
                        <>
                            <Space align="baseline" wrap>
                                {component}
                                <DeleteButton onClick={() => onDeleteIgnoreIdentifier(index)} disabled={isReadOnly} />
                            </Space>
                            <OrBlock>{tCommon('or')}</OrBlock>
                        </>
                    );
                })}
            </Form>

            <SecondaryButton
                disabled={isReadOnly}
                type="dashed"
                icon={<PlusCircleOutlined />}
                shape="default"
                onClick={onAddNewIgnoreChoice}
            >
                {t('add_new_ignore_option')}
            </SecondaryButton>
        </div>
    );
};

export default IgnoreOverview;
